import { countriesMapping } from './countriesMapping'
import type { AlegraCompany, AlegraUser, DerivedSessionFields } from '../types'

function getLocalStorage(key: string): string | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Pure function: turns a user + company payload into the derived session
 * fields. Reads `localStorage` only as a fallback for `appVersion` and
 * `userLanguage` — no side-effects (writes happen in `SessionStore.apply()`).
 *
 * Kept separate from `SessionStore` so it's trivial to unit-test and
 * tree-shake.
 */
export function derive(user: AlegraUser, company: AlegraCompany): DerivedSessionFields {
  const userMail = user.email ?? ''
  const userFullName = [user.name?.trim(), user.lastName?.trim()].filter(Boolean).join(' ')
  const userName = userFullName || userMail
  const membershipPlan = user.membership?.planKeyword ?? ''
  const appVersion =
    company.applicationVersion ?? getLocalStorage('al-company-appVersion') ?? 'other'
  const userLanguage = user.language ?? getLocalStorage('al-user-language') ?? 'es'

  return {
    userMail,
    userFullName,
    userName,
    membershipPlan,
    appVersion,
    userLanguage,
    userLenguage: userLanguage,
    userCountry: countriesMapping[appVersion] ?? 'INT',
    hasUser: Boolean(user.id),
    hasAlegraShop: Boolean(company.shop?.id),
    hasDistributor: Boolean(user.distributor?.id),
    hasOnlyInvoicePlan: membershipPlan.startsWith('only-invoicing'),
    hasReadonlyPlan: membershipPlan.startsWith('readonly'),
    interfaceSettings: company.interfaceSettings ?? false,
  }
}
